import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface RankedListRow {
  key: string;
  label: string;
  /** Drives the bar length and the primary right-hand number. */
  value: number;
  /** Secondary line under the label. */
  hint?: string;
  /** Overrides the default `share`-derived caption under the value. */
  meta?: string;
  /** Share of the whole, 0..1. Rendered as the caption when `meta` is unset. */
  share?: number;
  /** Optional external link on the label. */
  href?: string;
}

interface RankedListProps {
  rows: RankedListRow[];
  formatValue?: (value: number) => string;
  /** Hides hints and tightens spacing, for narrow side panels. */
  compact?: boolean;
  /** Shown when `rows` is empty. */
  emptyLabel?: string;
  className?: string;
  onRowClick?: (row: RankedListRow) => void;
}

/**
 * Horizontal bar ranking used across every tab of the Marketing Dashboard
 * (traffic sources, search queries, top posts, campaigns, countries).
 *
 * Bars are scaled against the largest row rather than against the total, so a
 * long tail stays readable — with total-relative scaling, a list where the top
 * item holds 60% renders every other bar as a sliver.
 */
export function RankedList({
  rows,
  formatValue = (v) => Math.round(v).toLocaleString(),
  compact,
  emptyLabel = 'No data for this period.',
  className,
  onRowClick,
}: RankedListProps) {
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  const max = Math.max(1, ...rows.map((r) => Math.abs(r.value)));

  return (
    <ul className={cn(compact ? 'space-y-2' : 'space-y-2.5', className)}>
      {rows.map((row) => {
        const body = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {row.href ? (
                    <a
                      href={row.href}
                      target="_blank"
                      rel="noreferrer"
                      className="underline-offset-2 hover:underline"
                      title={row.label}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.label}
                    </a>
                  ) : (
                    <span title={row.label}>{row.label}</span>
                  )}
                </p>
                {row.hint && !compact && (
                  <p className="truncate text-xs text-muted-foreground">{row.hint}</p>
                )}
              </div>
              <div className="shrink-0 text-right">
                <p className="tabular-nums text-sm text-foreground">{formatValue(row.value)}</p>
                {(row.meta || row.share != null) && (
                  <p className="text-[11px] tabular-nums text-muted-foreground">
                    {row.meta ?? `${((row.share ?? 0) * 100).toFixed(1)}%`}
                  </p>
                )}
              </div>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-old-growth/70"
                style={{ width: `${Math.max(2, (Math.abs(row.value) / max) * 100)}%` }}
              />
            </div>
          </>
        );

        return (
          <li key={row.key} className="space-y-1">
            {onRowClick ? (
              <button
                type="button"
                className="w-full rounded-md text-left transition-colors hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onRowClick(row)}
              >
                {body}
              </button>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface LegendDotProps {
  color: string;
  label: string;
  dashed?: boolean;
}

/** Chart legend swatch matching the Recharts stroke treatment. */
export function LegendDot({ color, label, dashed }: LegendDotProps) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className={cn(
          'inline-block h-0.5 w-4 rounded',
          dashed && 'border-t border-dashed bg-transparent',
        )}
        style={dashed ? { borderColor: color } : { backgroundColor: color }}
      />
      {label}
    </span>
  );
}

interface StatRowProps {
  label: string;
  value: ReactNode;
  hint?: string;
}

/** Compact label/value pair for dense side panels. */
export function StatRow({ label, value, hint }: StatRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-2 last:border-0">
      <div className="min-w-0">
        <p className="text-sm text-muted-foreground">{label}</p>
        {hint && <p className="truncate text-xs text-muted-foreground/80">{hint}</p>}
      </div>
      <p className="shrink-0 tabular-nums text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}
